/* ============================================================
   tc60-v5 · THE PEOPLE — "The Lineup"

   Seven named salespeople in one row, grouped by store. One panel open at
   a time; the Google review and that person's product pick live in a band
   below the row.

   Everything the section says is in CAST below, so real copy drops in
   without touching layout — same arrangement as WALL_ROWS in tc60-v3.js,
   and for the same reason: which person lands next to which is a
   composition decision, not something to derive from a loop.

   THE ONE THING TO KNOW BEFORE EDITING: `pick.product` must match a
   `.bundle-chip`'s `data-name` exactly. The pick button presses that chip
   rather than quoting a price, so this page still has exactly one price
   list. A typo there is a console warning and a dead button, which is the
   correct failure — better than a section quoting a number the calculator
   disagrees with.

   See SPEC-people-lineup.md for the design decisions and §7 for what is
   real vs placeholder.
   ============================================================ */
(function () {
  'use strict';

  var lineup = document.querySelector('[data-lineup]');
  if (!lineup) return;

  var CDN = 'https://cdn.shopify.com/s/files/1/0299/5830/7976/files/';
  /* resolved `asset_url` for Shanny's pick photo — see the data-px8-photo
     comment beside [data-lineup] in page.tc60.liquid for why this can't
     just be another CDN filename like the rest of pick.img. */
  var PX8_PHOTO = lineup.getAttribute('data-px8-photo') || '';
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var STARS = '★★★★★';
  /* The curly quotes around every review are REAL CHARACTERS in the text,
     not `.lq-text::before/::after` content as they were until 2026-08-03.
     Two reasons: generated content does not come along when a reader
     selects and copies the quote, and the pseudo-elements were needed for
     the hanging rule that now marks the band's quote as a quote — see
     tc60-v6.css §6. The v5 rules that generated them are overridden to
     `content: none` there; if this line is reverted, revert that too or
     every review shows two of each mark. */

  /* ----------------------------------------------------------
     THE CAST.

     REAL (do not invent over these): name, store, cred, and the product
     named in every pick.
       · Reuben holds the EXSA Excellence Award.
       · Shanny is THX-certified.
       · The other five are Best Customer Service (Singapore) nominees.

     PLACEHOLDER (Bryan is fact-checking): every `review`, every
     `pick.why`, and `role` — all seven are guessed as Sales Consultant.

     PHOTOGRAPHY. Four named files landed 2026-08-03 (Reuben, Zi Hao,
     Shanny, Zoe). Three are real portraits and are live; three people
     still borrow store crops and keep their PHOTO TBC label.

     `pos` is the object-position for the crop, and it is not decoration —
     these are wide candids being cut to roughly 2:5, so the default 50/50
     centre puts a shoulder where a face should be. Set per file:
       · reuben_exsa_1.jpg   portrait, face upper-centre → 48% 26%
       · Shanny_990f7ae3-5ae0-4590-bf94-030c59e57964.jpg (landed 2026-08-12,
                             replacing shanny_1.jpg below) — wide candid on a
                             showroom floor, Shanny at x≈60%, y≈35% → 58% 32%
       · zihao_adelphi_1.jpg landscape; Zi Hao is at x≈54% and a customer
                             fills the right third, so the crop is biased
                             LEFT to keep him and drop the customer's back
                             → 46% 34%
     ZOE WAS TBC, DELIBERATELY, UNTIL 2026-08-14. `TC_Acoustic_Photo.jpg`
     was a six-person group shot under the "Everyone's Favourite Company"
     neon — a genuinely good photograph, but there was no way to know
     which of the six was Zoe, and cropping a lineup panel onto the wrong
     person is the one mistake this section cannot make. It ran as her
     panel with a centred crop and the TBC label so the ask stayed visible.
     Resolved 2026-08-14: a named photo of Zoe (with a colleague, not
     solo) landed and replaced it — see her CAST entry below for the crop.

     SHANNY'S CREDENTIAL IS UNRESOLVED. His old photo (shanny_1.jpg, now
     replaced — see PHOTOGRAPHY above) held a legible certificate:
     "Excellent Service Award 2025 · Star Award — Chen Shan Yong, TC
     Acoustic". His `cred` here still reads THX Certified, which
     is what Bryan said, and it is left alone on purpose — reading a new
     credential off a photograph and promoting it to page copy is the same
     invention this section is trying to avoid. But EXSA was given as
     Reuben's award, so one of the two needs settling in the fact-check.

     CREDENTIAL MARKS: text only, deliberately. `logos/exsa.png` exists
     and `credMark` is wired for it, but putting the Straits Times Best
     Customer Service mark beside the word "Nominee" invites exactly the
     overclaim a screenshot would spread. Text for all seven keeps the
     siblings identical and keeps the claim exact. Set `credMark` when
     there are marks worth showing for all three credential types.
     ---------------------------------------------------------- */
  /* FINAL FIVE, 2026-08-12 — Bryan named the five to feature, originally in
     store-grouped order: Reuben, Jeremy (JTeo), Shanny, Zi Hao, Zoe (Sonos
     Flagship: Reuben/Jeremy; TC Acoustic Store: Shanny/Zi Hao; Zoe solo at
     the Experience Centre).

     REORDERED 2026-08-14 to Reuben, Jeremy, Zoe, Shanny, Zi Hao (Zoe
     centred, request: "shift zoe to the centre"). This breaks the
     store-grouping the order above happened to have, but the
     cluster-start divider logic (`CAST[i-1].store !== p.store`) doesn't
     require contiguous stores — it just compares each panel to its
     immediate predecessor, so Zoe now gets a divider on both sides
     (Flagship→Experience Centre, then Experience Centre→TC Acoustic
     Store) instead of the one she used to get at the end of the row.

     Franco and Nam (the two placeholder panels added 2026-08-07) are
     dropped — this pass is the real five, not a placeholder-padded seven.
     Reuben and Shanny return with real photos and real Google reviews
     supplied directly. reuben_exsa_1.jpg is the same file already
     documented above; Shanny's photo is a new file (see the PHOTOGRAPHY
     note above), replacing shanny_1.jpg, so its crop position is a fresh
     estimate rather than the one recorded there.

     Jeremy's review is also REPLACED here — was Eric Saint-Andre, Oct
     2025; now Orontius, also Oct 2025. Bryan supplied a different one for
     this pass; not a correction of the old one, just the version to run
     with.

     Reuben's, Jeremy's and Shanny's `pick.product` were set 2026-08-13:
     Arc Ultra, Era 300, Px8 S2 (was Era 100). No `why` copy came with the
     names, so each got one written in the same first-person, one-sentence
     voice as Zi Hao's and Zoe's picks below — flag if these need to be
     swapped for something the person actually said.

     Photos followed the same day ("add the product photos ... like how we
     did for Zi Hao"). Reuben's and Jeremy's reuse the matched lifestyle
     pair already on the CDN from the Sonos pathfinder (arc_ultra_38692...,
     era_300.png) rather than the plain white product cutouts also
     available (Arc_Ultra.png, white-SArcUltra.png) — every other pick on
     this row is a room photo, not a studio shot, and these two keep that
     rule. Shanny's is the theme's own tc60-px8-s2.jpg (a model shot,
     already used in the #promo product slider) — that one's a theme
     asset, not a CDN file, hence PX8_PHOTO/data-px8-photo above instead of
     a plain filename. Jeremy's old Era 100 photo (Era100_1.jpg) was
     dropped rather than left mismatched under the new Era 300 pick. */
  var CAST = [
    {
      name: 'Reuben', store: 'Sonos Flagship Store', role: 'Sales Executive',
      cred: 'EXSA Excellence Award',
      photo: 'reuben_exsa_1.jpg', pos: '48% 26%',
      review: {
        text: 'I recently visited TC Acoustic at Wheelock Place to explore the Sonos ecosystem and ended up purchasing two Sonos Era 100 speakers. The entire experience was excellent. I had a great demo where I could truly hear the clarity and depth of the speakers. What made the visit stand out was the conversation and assistance from Reuben. He was knowledgeable, patient, and never pushy. He took the time to understand what I was looking for and answered all my questions thoroughly, making the decision process easy and enjoyable. If you&rsquo;re looking for quality audio products and genuine, helpful advice, I highly recommend TC Acoustic and ask for Reuben. A top-notch retail experience.',
        by: 'Hady Am', when: 'Jan 2026'
      },
      pick: {
        /* Swapped from Stanmore IV to Arc Ultra 9.1.4 on request
           2026-08-14 (the full 9.1.4 Sonos Immersive Set: Arc Ultra, Sub
           and Era 300 Pair — see snippets/explore-bundle-dropdown.liquid's
           '9-1-4-sonos-immersive-set-with-arc-ultra-sub-gen-3-and-era-300-
           pair' handle). The marketing-carded studio shot flagged when
           this was first added (wordmark + feature badges burned in) was
           left off; a real lifestyle photo (Sonos_home_theatre.png) landed
           the same day, a proper room shot of the full setup — soundbar,
           both rear speakers and the couple on the couch — so it's used
           here like every other pick's photo. */
        product: 'Arc Ultra 9.1.4',
        img: 'https://cdn.shopify.com/s/files/1/0299/5830/7976/files/Sonos_home_theatre.png?v=1776824422',
        why: 'Money can&rsquo;t buy happiness - but a full Arc Ultra 9.1.4 setup can get you close.'
      }
    },
    {
      name: 'Jeremy', store: 'Sonos Flagship Store', role: 'Sales Executive',
      cred: 'Nominee · Best Customer Service, Singapore',
      photo: 'jeremy_jteo.jpg', pos: '50% 30%',
      review: {
        /* Replaced 2026-08-12 — see the FINAL FIVE note above. */
        text: 'Popped into the TC Acoustics Sonos Flagship at Wheelock Place and was blown away. The store layout feels like stepping into a live soundscape. Every demo zone lets you feel the music the way it&rsquo;s meant to be heard. Big shout-out to Mac and Jeremy from the team. Both were next-level helpful: super knowledgeable, patient with all my questions, and zero pushy sales vibe. They walked me through multi-room setups, gave smart comparisons, and made sure I left knowing exactly what would fit my space. If you&rsquo;re serious about audio or just curious, this is the spot. Stellar service, gorgeous space, and sound that will spoil you forever. Highly recommend.',
        by: 'Orontius', when: 'Oct 2025'
      },
      pick: {
        /* Swapped from Era 300 to Arc Ultra on request 2026-08-13 — reuses
           the photo Reuben's old Arc Ultra pick used above. */
        product: 'Arc Ultra', img: 'arc_ultra_38692d0b-0f28-4765-8b85-2820960f46ab.png',
        why: 'Pair it with a Sub 4 under the TV and the room fills in on the low end.'
      }
    },
    {
      name: 'Zoe', store: 'TC Experience Centre', role: 'Serving Happiness Executive',
      cred: 'Nominee · Best Customer Service, Singapore',
      /* Real photo landed 2026-08-14, replacing the six-person group shot
         (TC_Acoustic_Photo.jpg — see the PHOTOGRAPHY note above, now
         resolved). Still a candid with a colleague rather than solo, so
         `pos` is biased hard right/upper to keep her face in a 2:5 crop
         and drop the other person: she's at roughly x≈78%, y≈37% in the
         4000x3000 source, pushed further right again on request
         2026-08-14 ("shift Zoe's photo right more"). */
      photo: '20260814_172302_1.jpg', pos: '92% 36%',
      review: {
        text: 'TC and Sonos has been amazing. Zoe was super helpful throughout the whole process of helping us replace our out of warranty sub. In fact this is the second time Sonos has generously helped us with the replacement, which far exceeded my expectations and really shows their commitment to their products. This whole experience was great, which made me decided to choose their products over their competitors.',
        by: 'Chong Clarence', when: 'Jul 2025'
      },
      pick: {
        /* Swapped from Pi8 Earbuds to Major V on request 2026-08-13 —
           reuses the same lifestyle photo already on the CDN for the
           #promo product slider's Major V card. */
        product: 'Major V',
        img: 'https://cdn.shopify.com/s/files/1/0299/5830/7976/files/Lookbook_Major_V_cream_desktop_e3a3a8b1-6994-4884-ab43-515739efc25f.jpg?width=500',
        why: 'I love how versatile the Major V is — pocket-size, lightweight, and it comes everywhere with me 🎧'
      }
    },
    {
      name: 'Shanny', store: 'TC Acoustic Store', role: 'Sales Executive',
      cred: 'THX Certified',
      photo: 'Shanny_990f7ae3-5ae0-4590-bf94-030c59e57964.jpg', pos: '58% 32%',
      review: {
        text: 'We were served by Shan Yong at TC Acoustic @ The Adelphi, and the experience was excellent. He introduced us to the various Marshall soundbar with a clear and engaging explanation. His product knowledge and patience stood out, making it easy for us to understand and experience the sound quality. He was never pushy, just genuinely helpful. Overall, we appreciated his professionalism and friendly approach. Highly recommend looking for Shan Yong if you&rsquo;re visiting TC Acoustic @ The Adelphi.',
        by: 'Juen Kit Poon', when: 'Sep 2025'
      },
      pick: {
        product: 'Px8 S2', img: PX8_PHOTO, pos: 'center 20%',
        why: 'Business class sound. The Px8 S2 spoils you and never lets you go back.'
      }
    },
    {
      name: 'Zi Hao', store: 'TC Acoustic Store', role: 'Sales Executive',
      cred: 'Nominee · Best Customer Service, Singapore',
      photo: 'zihao_adelphi_1.jpg', pos: '46% 34%',
      review: {
        text: 'Thank you Hao for the extreme patience. I bought a Sonos home theater system 3 years ago and recently upgraded to Era 300 rear speakers. For a complex smart home system like mine which includes Hue Sync Lights, HD boxes and 4K TV sticks, there were loads of handshake issues that caused me a lot of angst (and agony) but Hao guided me through all possible scenarios to troubleshoot and helped replace parts where required and even took time off on a weekend to come to my house to assess the diagnostics. As an audiophile, the system is crucial to me and I am glad that Hao is always patient and helpful through these years. Top-notch customer service: thank you Hao for always going above and beyond.',
        by: 'Dillon Tan', when: 'Jan 2026'
      },
      pick: {
        /* Swapped from Zeppelin to Heston 120 on request 2026-08-13; no
           Heston 120 asset existed in this theme at the time, so no
           photo. Added 2026-08-14, same source and same caveat as
           Reuben's Stanmore IV above — the live storefront's studio
           webstore shot, not a lifestyle room photo. `pos` biases the
           crop up, away from the Bluetooth/WiFi/Dolby Atmos badge row
           along the bottom of the source. */
        product: 'Heston 120',
        img: 'https://cdn.shopify.com/s/files/1/0299/5830/7976/files/3_Heston120Black_Sub200BlackUSP.jpg?v=1769574773',
        pos: 'center 20%',
        why: 'Marshall&rsquo;s own soundbar, same sound signature, spread across the room instead of just the amp.'
      }
    }
  ];

  /* ----------------------------------------------------------
     BUILD THE ROW
     ---------------------------------------------------------- */
  var DEAL_STEP = 60;   /* ms between panels dealing in */

  /* Mobile carries the band's copy inside each card (the band is display:none
     under 760). Built for every panel and hidden by CSS at the other tier
     rather than branched on width here — a resize must not need a rebuild. */
  /* The product shot beside the pick.

     ALL SEVEN ARE NOW LIFESTYLE PHOTOGRAPHS (Bryan, 2026-08-03: "can it all
     be lifestyle photos instead?"). That single change deleted a whole class
     of problem. The old set was five transparent cut-outs plus two room
     shots, which needed two `object-fit` rules, a LIGHT well (because a
     transparent black soundbar is invisible on ink) and 9px of padding to
     keep cut-outs off the edges — and it still could not make the Arc read.
     Seven photographs want exactly one rule: `cover`, no padding, no ground,
     because the photograph IS the ground. `scene` is gone from the CAST.

     Two fields carry what a photograph needs and a cut-out did not:

     · `imgq` — a Shopify image query override. Three of the seven are B&W
       marketing cards with a headline burned into the bottom of the frame
       (Zeppelin "Form Meets Function", Px7 S3 and Pi8 "Active Noise
       Cancellation"). `crop=top` with a shortened height removes the text
       band. THE TRAP: Shopify silently DROPS `crop` if `width` exceeds the
       source width, and these three sources are only 1000px — asking for
       width=1600 returns the full uncropped square with the text still in
       it, HTTP 200 and all. Keep these at width=500.
     · `pos` — object-position. A cut-out centred itself; a room shot has a
       subject somewhere specific in the frame, and a centre crop of the Px7
       photograph lands on the model's cheek rather than the headphones. */
  function pickShot(p) {
    if (!p.pick.img) return '';
    /* PX8_PHOTO is already a full, resolved `asset_url` (its own querystring
       and all) rather than a bare CDN filename — prefixing CDN or appending
       imgq onto it would produce a broken URL, so absolute URLs pass
       through untouched instead of going through the usual CDN+imgq build. */
    var abs = /^(https?:)?\/\//.test(p.pick.img);
    var src = abs ? p.pick.img : CDN + p.pick.img + (p.pick.imgq || '?width=500');
    return '<span class="lpick-shot">' +
      '<img src="' + src +
        '" alt="' + p.pick.product + '" loading="lazy"' +
        (p.pick.pos ? ' style="object-position:' + p.pick.pos + '"' : '') + '>' +
      '</span>';
  }

  function pickBlock(p) {
    if (!p.pick) return '';
    return '' +
      '<p class="lpick-lbl">' + p.name + '’s pick</p>' +
      '<div class="lpick-row">' +
        pickShot(p) +
        '<div class="lpick-copy">' +
          '<p class="lpick-prod">' + p.pick.product + '</p>' +
          '<p class="lpick-why">' + p.pick.why + '</p>' +
          /* was a button pressing a `.bundle-chip` — the calculator it
             pressed is gone (2026-08-05, replaced by the #promo product
             slider + paperform embed), so this just links to the offers
             section instead of wiring to a control that no longer exists. */
          '<a class="lpick-go" href="#promo">' +
            'See it in TC60 Offers <span class="arrow">→</span>' +
          '</a>' +
        '</div>' +
      '</div>';
  }

  /* READ MORE, added 2026-08-05 -- the real reviews run much longer than
     the placeholders they replaced (Reuben's and Zi Hao's both run six-
     plus sentences), and a fixed-height panel/band can't just grow to fit
     whichever one is open without the row jumping around under the
     visitor's mouse. `.lq-clamp` (tc60-v7.css) line-clamps to a few lines;
     this toggle is a `role="button"` SPAN, not a real `<button>` -- `.lp`
     (the whole panel) already IS a button, and a nested button silently
     corrupts that parse. wireReadMore() only reveals the toggle when the
     text actually overflows its clamp, so a short review doesn't grow a
     "Read more" that has nothing to show. */
  function moreToggle() {
    return '<span class="lq-more" role="button" tabindex="0" hidden>Read more</span>';
  }

  function wireReadMore(root) {
    var q = root && root.querySelector('.lq-text.lq-clamp');
    var btn = root && root.querySelector('.lq-more');
    if (!q || !btn) return;
    q.classList.remove('is-expanded');
    btn.textContent = 'Read more';
    btn.hidden = true;
    requestAnimationFrame(function () {
      if (q.scrollHeight - q.clientHeight > 2) btn.hidden = false;
    });
  }

  /* Country ports (MY/HK/TH) name staff before their Google reviews and
     product picks are ready \u2014 `review`/`pick` are optional on CAST entries
     so a panel can go live as "name only" rather than block the section on
     content that isn't fact-checked yet. See tc60-v7.css \u00A725/26. */
  function inlineCopy(p) {
    if (!p.review) {
      return '<div class="lp-inline"><p class="lq-text lq-pending">Review coming soon.</p>' +
        (p.pick ? '<div class="lpick">' + pickBlock(p) + '</div>' : '') +
        '</div>';
    }
    return '' +
      '<div class="lp-inline">' +
        '<p class="lq-text lq-clamp">\u201C' + p.review.text + '\u201D</p>' +
        moreToggle() +
        '<div class="lq-by">' +
          '<span class="lq-stars" aria-hidden="true">' + STARS + '</span>' +
          '<cite class="lq-cite">' + p.review.by + '</cite>' +
        '</div>' +
        (p.pick ? '<div class="lpick">' + pickBlock(p) + '</div>' : '') +
      '</div>';
  }

  var panels = [];

  CAST.forEach(function (p, i) {
    var b = document.createElement('button');
    b.type = 'button';
    b.className = 'lp';
    b.setAttribute('aria-expanded', 'false');
    /* The panel names the person, not "panel 4 of 7" — a screen reader
       user should hear who they are about to open. */
    b.setAttribute('aria-label', p.name + ' — ' + p.role + ', ' + p.store);
    if (i > 0 && CAST[i - 1].store !== p.store) b.dataset.clusterStart = 'true';
    if (!reduce) b.style.transitionDelay = (i * DEAL_STEP) + 'ms';

    b.innerHTML = '' +
      (p.photo ?
        '<img src="' + CDN + p.photo + '?width=900" alt="" loading="lazy"' +
          (p.pos ? ' style="object-position:' + p.pos + '"' : '') + '>' +
        (p.photoTBC ? '<span class="lp-tbc">Photo TBC</span>' : '')
      : '<span class="lp-photo-placeholder" aria-hidden="true"></span><span class="lp-tbc">Photo TBC</span>') +
      '<span class="lp-id">' +
        /* the orange `.lp-cred` award/nominee liner (EXSA Excellence
           Award, THX Certified, etc.) was removed on request 2026-08-13.
           `cred`/`credMark` stay on CAST below since they're still real,
           fact-checked data — just no longer rendered here. */
        '<span class="lp-nm">' + p.name + '</span>' +
        /* The separator lives inside .lp-role with a hard space on each side.
           A plain " · " leading the span collapsed at the inline-block
           boundary and rendered "THE ADELPHI· SALES CONSULTANT". "Sales
           Executive" itself stopped rendering on request 2026-08-13 — `role`
           stays on CAST below (Zoe's distinct title still needs it) but the
           generic "Sales Executive" tag no longer shows. */
        '<span class="lp-meta">' + p.store +
          (p.role !== 'Sales Executive' ? '<span class="lp-role">&nbsp;·&nbsp;' + p.role + '</span>' : '') +
        '</span>' +
        inlineCopy(p) +
      '</span>';

    lineup.appendChild(b);
    panels.push(b);
  });

  /* SG's 5-across row is full-bleed by design, but a country port whose
     cast hasn't reached five yet (MY's 2, HK's TBC) doesn't have enough
     people to fill that same edge-to-edge width without
     each panel stretching into a wide, flattened banner instead of a
     portrait. `.lineup--compact` (tc60-v7.css §27) caps panel width
     instead of letting flex-grow fill the row, so a short cast reads as a
     shorter row rather than a stretched one. Threshold is CAST.length, not
     a per-country flag, so a page just grows out of compact mode on its
     own once its real cast reaches five. */
  if (CAST.length && CAST.length < 5) lineup.classList.add('lineup--compact');

  /* ----------------------------------------------------------
     THE BAND — review left, pick right, swapping with the selection.
     ---------------------------------------------------------- */
  var band     = document.querySelector('[data-band]');
  var bandQ    = band && band.querySelector('[data-band-quote]');
  var bandPick = band && band.querySelector('[data-band-pick]');

  var CHAR_STAGGER = 18;   /* ms; the reference used 6 for a long quote */

  /* Per-character rise, from the ScrollReelTestimonials reference. Only the
     product name gets it — see the note in tc60-v5.css. Words are wrapped
     so a long product name still breaks between words, not mid-word. */
  function chars(text) {
    var out = '', idx = 0;
    text.split(' ').forEach(function (word, wi, all) {
      out += '<span style="display:inline-block;white-space:nowrap">';
      Array.prototype.forEach.call(word, function (ch) {
        out += '<span class="lch" style="animation-delay:' + (idx * CHAR_STAGGER) + 'ms">' + ch + '</span>';
        idx++;
      });
      out += '</span>';
      if (wi < all.length - 1) { out += ' '; idx++; }
    });
    return out;
  }

  function renderBand(p) {
    if (!bandQ || !bandPick) return;
    if (!p.review) {
      bandQ.innerHTML = '<p class="lq-text lq-pending">Review coming soon.</p>';
    } else {
      bandQ.innerHTML = '' +
        '<p class="lq-text lq-clamp">\u201C' + p.review.text + '\u201D</p>' +
        moreToggle() +
        '<div class="lq-by">' +
          '<span class="lq-stars" aria-hidden="true">' + STARS + '</span>' +
          '<cite class="lq-cite">' + p.review.by + '</cite>' +
        '</div>';
      wireReadMore(bandQ);
    }
    /* Same block as the mobile card, then the product name is swapped for
       its per-character version. Built once and patched rather than
       duplicated, so the desktop band and the mobile card cannot drift. */
    bandPick.innerHTML = p.pick ? pickBlock(p) : '';
    if (p.pick && !reduce) {
      var prod = bandPick.querySelector('.lpick-prod');
      if (prod) prod.innerHTML = chars(p.pick.product);
    }
  }

  var SWAP_MS = 340;   /* matches .lband.is-swapping's transition */
  var swapTimer = null;
  var openIdx = -1;

  function open(i, focus) {
    if (i === openIdx || !panels[i]) return;
    openIdx = i;

    panels.forEach(function (el, n) {
      var on = n === i;
      el.classList.toggle('is-open', on);
      el.setAttribute('aria-expanded', on ? 'true' : 'false');
    });
    lineup.classList.add('has-open');
    if (focus) panels[i].focus();
    /* mobile's inline copy was hidden (0 height) while its panel was closed,
       so its own overflow check ran against nothing — redo it now that the
       panel is visible. */
    wireReadMore(panels[i]);

    if (!band) return;
    if (reduce) { renderBand(CAST[i]); return; }

    /* Old block out, new block in — never both on screen at once, which is
       the one thing the reference component gets right and most crossfades
       get wrong. */
    band.classList.add('is-swapping');
    clearTimeout(swapTimer);
    swapTimer = setTimeout(function () {
      renderBand(CAST[i]);
      band.classList.remove('is-swapping');
    }, SWAP_MS);
  }

  panels.forEach(function (el, i) {
    el.addEventListener('click', function () { open(i); });
  });

  /* Arrow keys walk the row; Home/End jump to the ends. Panels are real
     buttons, so Tab already works and this is navigation on top of it. */
  lineup.addEventListener('keydown', function (e) {
    var k = e.key, next = null;
    if (k === 'ArrowRight') next = Math.min(openIdx + 1, panels.length - 1);
    else if (k === 'ArrowLeft') next = Math.max(openIdx - 1, 0);
    else if (k === 'Home') next = 0;
    else if (k === 'End') next = panels.length - 1;
    if (next === null) return;
    e.preventDefault();
    open(next, true);
  });

  /* ----------------------------------------------------------
     READ MORE — toggles the clamp on whichever quote the click landed in.
     Delegated once at the document rather than per-panel, since panels are
     rebuilt/reopened repeatedly and a `.lq-more` is recreated every render.
     ---------------------------------------------------------- */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('.lq-more');
    if (!btn) return;
    var q = btn.previousElementSibling;
    if (!q || !q.classList.contains('lq-text')) return;
    var expanded = q.classList.toggle('is-expanded');
    btn.textContent = expanded ? 'Show less' : 'Read more';
  });
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    if (!e.target.classList || !e.target.classList.contains('lq-more')) return;
    e.preventDefault();
    e.target.click();
  });

  /* ----------------------------------------------------------
     DEAL-IN, then hand the transition back to flex-grow.

     The inline transition-delay that staggers the deal would also delay
     every later open, so it is stripped once the deal has finished.
     ---------------------------------------------------------- */
  var dealt = false;
  function deal() {
    if (dealt) return;
    dealt = true;
    lineup.classList.add('is-dealt');
    var settle = panels.length * DEAL_STEP + 600;
    setTimeout(function () {
      panels.forEach(function (el) { el.style.transitionDelay = ''; });
    }, reduce ? 0 : settle);
  }

  if (reduce || !('IntersectionObserver' in window)) {
    deal();
  } else {
    new IntersectionObserver(function (es, io) {
      if (es[0].isIntersecting) { deal(); io.disconnect(); }
    }, { rootMargin: '0px 0px -12% 0px' }).observe(lineup);
    /* Same safety net as tc60.js's reveal: never leave the row invisible. */
    setTimeout(deal, 3000);
  }

  /* Reuben opens first — he is now the first panel, and an empty band
     under a row of closed panels reads as a loading state. */
  open(0);
})();
